import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MaskedPhoneInput, MaskedDateOfBirthInput, MaskedZipInput } from '@/shared/inputs';
import { PasswordHelper } from '@/components/PasswordHelper';
import * as yup from 'yup';

export const EMAIL_KEY = 'email';
export const RETYPE_EMAIL_KEY = 'retype_email';
export const PHONE_KEY = 'phone';
export const PASSWORD_KEY = 'password';
export const RETYPE_PASSWORD_KEY = 'retype_password';
export const FIRST_NAME_KEY = 'first_name';
export const LAST_NAME_KEY = 'last_name';
export const DATE_OF_BIRTH_KEY = 'date_of_birth';
export const ZIP_CODE_KEY = 'zipcode';
export const MBI_KEY = 'mbi';
export const GENDER_KEY = 'gender';

export const fieldKeyToTransKey = {
  [EMAIL_KEY]: 'form.validations.email',
  [RETYPE_EMAIL_KEY]: 'form.validations.email',
  [PHONE_KEY]: 'form.validations.phone',
  [PASSWORD_KEY]: 'form.validations.password',
  [RETYPE_PASSWORD_KEY]: 'form.validations.retype-password',
};

// CMS Medicare Beneficiary Identifier: 11 chars, pattern C A AN N A AN N A A N N.
// Letters exclude S, L, O, I, B, Z (avoid digit confusion). Example: 1EG4TE5MK73.
const MBI_LETTER = '[ACDEFGHJKMNPQRTUVWXY]';
const MBI_ALNUM = '[ACDEFGHJKMNPQRTUVWXY0-9]';
export const MBI_REGEX = new RegExp(
  `^[1-9]${MBI_LETTER}${MBI_ALNUM}[0-9]${MBI_LETTER}${MBI_ALNUM}[0-9]${MBI_LETTER}${MBI_LETTER}[0-9][0-9]$`,
);

// Canonical MBI form: strip every non-alphanumeric char (ASCII hyphen, Unicode
// en/em dashes, spaces, dots a user may paste from a card or PDF) and uppercase.
// Shared by the Yup transform and MedicareCoverageStep so the validated value and
// the stored/transmitted value can never diverge.
export const normalizeMbi = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : '';

export const useFormValues = (fieldKeys: string[]) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const formValues: Record<string, any> = {};

    fieldKeys.forEach((key) => {
      switch (key) {
        case EMAIL_KEY:
          formValues[key] = {
            label: t('create-account.email'),
            type: 'email',
            initialValue: '',
            privateInput: false,
            validationSchema: yup
              .string()
              .required(t('form.validations.required'))
              .email(t('form.validations.email')),
            inputmode: 'email' as const,
            autoComplete: 'email',
            hint: 'create-account.email-hint',
          };
          break;

        case RETYPE_EMAIL_KEY:
          formValues[key] = {
            label: t('create-account.retype-email'),
            type: 'email',
            initialValue: '',
            privateInput: false,
            validationSchema: yup
              .string()
              .required(t('form.validations.required'))
              .email(t('form.validations.email'))
              .oneOf([yup.ref(EMAIL_KEY)], t('form.validations.email-match')),
            inputmode: 'email' as const,
            autoComplete: 'email',
          };
          break;

        case PHONE_KEY:
          formValues[key] = {
            label: t('create-account.phone-number'),
            type: 'tel',
            initialValue: '',
            privateInput: false,
            validationSchema: yup
              .string()
              .required(t('form.validations.required'))
              .matches(/^\d{10}$/, t('form.validations.phone')),
            inputmode: 'tel' as const,
            autoComplete: 'tel',
            inputComponent: MaskedPhoneInput,
          };
          break;

        case PASSWORD_KEY:
          formValues[key] = {
            label: t('create-account.password'),
            type: 'password',
            initialValue: '',
            privateInput: true,
            validationSchema: yup
              .string()
              .required(t('form.validations.required'))
              .min(8, t('form.validations.password-length'))
              .matches(/[a-z]/, t('form.validations.password-lowercase'))
              .matches(/[A-Z]/, t('form.validations.password-uppercase'))
              .matches(
                /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/,
                t('form.validations.password-special'),
              ),
            autoComplete: 'new-password',
            HelperComponent: PasswordHelper,
          };
          break;

        case RETYPE_PASSWORD_KEY:
          formValues[key] = {
            label: t('create-account.retype-password'),
            type: 'password',
            initialValue: '',
            privateInput: true,
            validationSchema: yup
              .string()
              .required(t('form.validations.required'))
              .oneOf([yup.ref(PASSWORD_KEY)], t('form.validations.password-match')),
            autoComplete: 'new-password',
          };
          break;

        case FIRST_NAME_KEY:
          formValues[key] = {
            label: t('medicare-coverage.first-name'),
            type: 'text',
            initialValue: '',
            privateInput: false,
            validationSchema: yup.string().required(t('form.validations.required')),
            autoComplete: 'given-name',
            halfWidth: true,
          };
          break;

        case LAST_NAME_KEY:
          formValues[key] = {
            label: t('medicare-coverage.last-name'),
            type: 'text',
            initialValue: '',
            privateInput: false,
            validationSchema: yup.string().required(t('form.validations.required')),
            autoComplete: 'family-name',
            halfWidth: true,
          };
          break;

        case DATE_OF_BIRTH_KEY:
          formValues[key] = {
            label: t('medicare-coverage.date-of-birth'),
            type: 'text',
            initialValue: '',
            privateInput: false,
            placeholder: 'MM/DD/YYYY',
            validationSchema: yup
              .string()
              .required(t('form.validations.dob'))
              .test(
                'dob-length',
                t('form.validations.dob'),
                (value) => !value || value.length === 10,
              )
              .test('dob-age', t('form.validations.dob-age'), (value) => {
                if (!value || value.length !== 10) return true;
                const [month, day, year] = value.split('/');
                const birthDate = new Date(
                  parseInt(year, 10),
                  parseInt(month, 10) - 1,
                  parseInt(day, 10),
                );
                const age = Math.floor((Date.now() - birthDate.getTime()) / 31557600000);
                return age >= 18 && age <= 120;
              }),
            autoComplete: 'bday',
            inputComponent: MaskedDateOfBirthInput,
            halfWidth: true,
          };
          break;

        case ZIP_CODE_KEY:
          formValues[key] = {
            label: t('medicare-coverage.zip-code'),
            type: 'text',
            initialValue: '',
            privateInput: false,
            validationSchema: yup
              .string()
              .required(t('form.validations.zip'))
              .length(5, t('form.validations.zip')),
            autoComplete: 'postal-code',
            inputComponent: MaskedZipInput,
            halfWidth: true,
          };
          break;

        case MBI_KEY:
          formValues[key] = {
            label: t('medicare-coverage.mbi'),
            type: 'text',
            initialValue: '',
            privateInput: false,
            validationSchema: yup
              .string()
              .transform((value) => normalizeMbi(value))
              .required(t('form.validations.required'))
              .matches(MBI_REGEX, t('form.validations.mbi')),
          };
          break;

        case GENDER_KEY:
          formValues[key] = {
            label: t('medicare-coverage.gender'),
            type: 'select',
            select: true,
            initialValue: '',
            privateInput: false,
            validationSchema: yup.string().required(t('form.validations.required')),
            selectOptionEntries: [
              { value: 'male', label: t('medicare-coverage.gender-male') },
              { value: 'female', label: t('medicare-coverage.gender-female') },
              { value: 'other', label: t('medicare-coverage.gender-other') },
              { value: 'unknown', label: t('medicare-coverage.gender-prefer-not-to-say') },
            ],
          };
          break;

        default:
          break;
      }
    });

    return formValues;
  }, [t, fieldKeys]);
};
